import { BCExtremeGridEntities } from '@bimser/components';
import { createGuid, IDictionary, isNullOrUndefined } from '@bimser/core';
import DataGrid from '../..';
import { IUserEvent } from '../../../../../../entities/services';
import {
	IControlExecutedServerEvent,
	IRowInsertedEventArgs,
	IRowInsertingEventArgs,
	IRowRemovedEventArgs,
	IRowRemovingEventArgs,
	IRowUpdatedEventArgs,
	IRowUpdatingEventArgs
} from '../../../../entities/EventArgs';
import { DataGridCellType } from '../../entities';
import _isEqualWith from 'lodash/isEqualWith';
import _isNaN from 'lodash/isNaN';

function onGridUpdated(
	this: DataGrid,
	props: BCExtremeGridEntities.IExtremeGridProps,
	prevProps: BCExtremeGridEntities.IExtremeGridProps
) {
	if (this.dataSourceType == 'relation') {
		triggerRowEvents.call(this, props, prevProps);
	}
}

export function triggerRowEvents(
	this: DataGrid,
	props: BCExtremeGridEntities.IExtremeGridProps,
	prevProps: BCExtremeGridEntities.IExtremeGridProps
) {
	const uniqueDataField = this.getUniqueDataField();
	const rows = props.dataSource.data;
	const oldRows = prevProps.dataSource.data;

	if (oldRows?.length < rows?.length) {
		const newRow = rows.find(row =>
			oldRows.every(oldRow => oldRow[uniqueDataField] !== row[uniqueDataField])
		);

		onRowInserted.call(this, { row: newRow });
	} else if (oldRows?.length > rows?.length) {
		const deletedRow = oldRows.find(oldRow =>
			rows.every(row => oldRow[uniqueDataField] !== row[uniqueDataField])
		);

		onRowRemoved.call(this, { row: deletedRow });
	} else {
		const args: any = {};
		const customizer = (rowValue: any, oldRowValue: any) =>
			(isNullOrUndefined(rowValue) && isNullOrUndefined(oldRowValue)) ||
			(_isNaN(rowValue) && _isNaN(oldRowValue))
				? true
				: undefined;
		args.newRow = rows.find(row => {
			const oldRow = oldRows.find(oldR => oldR[uniqueDataField] == row[uniqueDataField]);
			const result = !_isEqualWith(row, oldRow, customizer);
			args.oldRow = result ? oldRow : [];
			return result;
		});

		if (args.newRow) {
			onRowUpdated.call(this, args);
		}
	}
}

function onRowDoubleClick(this: DataGrid, row: any) {
	this.emit('event:OnRowDoubleClick', { row });
}

async function onRowRemoving(this: DataGrid, valueArgs: any): Promise<boolean | void> {
	await this.emit('event:OnRowRemoving', valueArgs);

	if (!valueArgs.cancel) {
		const args = await this.emit<IUserEvent<IRowRemovingEventArgs>>(
			'event:server:OnRowRemoving',
			{
				argument: valueArgs,
				controlId: this.name,
				name: 'OnRowRemoving',
				sender: 'web',
				guid: createGuid()
			}
		);

		const executedServerEvent: IControlExecutedServerEvent = args[0]?.executedServerEvent;

		if (!executedServerEvent || !executedServerEvent?.parameters.cancel) {
			return Promise.resolve();
		} else {
			return true;
		}
	} else {
		return true;
	}
}

async function onRowUpdating(
	this: DataGrid,
	valueArgs: IRowUpdatingEventArgs
): Promise<boolean | void> {
	const row = {
		...valueArgs.oldRow,
		...valueArgs.newRow
	};

	valueArgs.newRow = this.checkColumnFormulas(row);

	await this.emit('event:OnRowUpdating', valueArgs);

	if (!valueArgs.cancel) {
		const args = await this.emit<IUserEvent<any>>('event:server:OnRowUpdating', {
			argument: valueArgs,
			controlId: this.name,
			name: 'OnRowUpdating',
			sender: 'web',
			guid: createGuid()
		});

		const executedServerEvent: IControlExecutedServerEvent = args[0]?.executedServerEvent;

		if (!executedServerEvent || !executedServerEvent?.parameters.cancel) {
			return Promise.resolve();
		} else {
			return true;
		}
	} else {
		return true;
	}
}

async function onRowInserting(this: DataGrid, valueArgs: any): Promise<boolean | void> {
	this.checkColumnFormulas(valueArgs.row);

	await this.emit('event:OnRowInserting', valueArgs);

	if (!valueArgs.cancel) {
		const args = await this.emit<IUserEvent<IRowInsertingEventArgs>>(
			'event:server:OnRowInserting',
			{
				argument: valueArgs,
				controlId: this.name,
				name: 'OnRowInserting',
				sender: 'web',
				guid: createGuid()
			}
		);

		const executedServerEvent: IControlExecutedServerEvent = args[0]?.executedServerEvent;

		if (!executedServerEvent || !executedServerEvent?.parameters.cancel) {
			return Promise.resolve();
		} else {
			return true;
		}
	} else {
		return true;
	}
}

async function onRowInserted(
	this: DataGrid,
	{ row }: { row: IDictionary<any>; rows: Array<IDictionary<any>> }
) {
	row = getBaseRowData.call(this, row);
	this.emit('event:OnRowInserted', { row });
	await this.emit<IUserEvent<IRowInsertedEventArgs>>('event:server:OnRowInserted', {
		argument: { row },
		controlId: this.name,
		name: 'OnRowInserted',
		sender: 'web',
		guid: createGuid()
	});
	this.reload(true);
}

async function onRowUpdated(
	this: DataGrid,
	args: { newRow: IDictionary<any>; oldRow: IDictionary<any>; rows: Array<IDictionary<any>> }
) {
	args.newRow = getBaseRowData.call(this, args.newRow);
	args.oldRow = getBaseRowData.call(this, args.oldRow);
	this.emit('event:OnRowUpdated', { newRow: args.newRow, oldRow: args.oldRow });
	await this.emit<IUserEvent<IRowUpdatedEventArgs>>('event:server:OnRowUpdated', {
		argument: { newRow: args.newRow, oldRow: args.oldRow },
		controlId: this.name,
		name: 'OnRowUpdated',
		sender: 'web',
		guid: createGuid()
	});
	this.reload(true);
}

async function onRowRemoved(
	this: DataGrid,
	{ row }: { row: IDictionary<any>; rows: Array<IDictionary<any>> }
) {
	row = getBaseRowData.call(this, row);
	this.emit('event:OnRowRemoved', { row });
	await this.emit<IUserEvent<IRowRemovedEventArgs>>('event:server:OnRowRemoved', {
		argument: { row },
		controlId: this.name,
		name: 'OnRowRemoved',
		sender: 'web',
		guid: createGuid()
	});
	this.reload(true);
}

export function getBaseRowData(this: DataGrid, data) {
	return (
		this.columns
			?.map(({ name }) => name)
			?.reduce((result, name) => {
				let value = isNullOrUndefined(data?.[name]) ? null : data[name];

				if (
					this.columns.find(column => column.name === name)?.cellType ===
						DataGridCellType.boolean &&
					isNullOrUndefined(value)
				) {
					value = false;
				}

				return { ...result, [name]: value };
			}, data) || data
	);
}

export {
	onGridUpdated,
	onRowRemoved,
	onRowUpdated,
	onRowInserted,
	onRowInserting,
	onRowUpdating,
	onRowRemoving,
	onRowDoubleClick
};
